import { ArrowUpRight, Anchor, Ship } from "lucide-react";
import TrackWidget from "./TrackWidget";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/2da4906e-da56-4c5d-bd4c-278bf8ff8295/images/0b1271d54ccdf63eea8ea19ebb450e70dfb38a4c7697497da8bc3e9d6f24c761.png";

export default function Hero() {
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative isolate pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-24 lg:pb-32 overflow-hidden grain"
        >
            {/* Background image + overlay */}
            <div className="absolute inset-0 -z-10">
                <img
                    src={HERO_IMG}
                    alt="Australia supply chain command center"
                    className="w-full h-full object-cover opacity-55"
                />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 grid-backdrop opacity-70" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="section-tag" data-testid="hero-eyebrow">
                                <span className="pulse-dot" />
                                Australian Logistics Tech
                            </span>
                            <span className="hidden sm:inline-flex font-mono-accent text-[11px] text-zinc-500">
                                AU · IMPORTERS · EXPORTERS
                            </span>
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-display text-4xl sm:text-5xl lg:text-7xl text-white leading-[0.95] tracking-tighter"
                        >
                            Take control of <span className="text-glow-cyan">freight cost</span>,
                            shipment visibility &amp; supply&nbsp;chain performance.
                        </h1>

                        <p
                            data-testid="hero-subheadline"
                            className="mt-8 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed"
                        >
                            WE DELVR LOGISTICS PTY LTD delivers solutions for the unique logistics
                            problems faced by Australian importers and exporters. Improve
                            visibility, manage freight cost and drive continuous improvement —
                            <span className="text-zinc-200"> before, during and after shipment.</span>
                        </p>

                        <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-ctas">
                            <a
                                href="#early-access"
                                className="btn-primary"
                                data-testid="hero-cta-primary"
                            >
                                Join Early Access
                                <ArrowUpRight size={18} />
                            </a>
                            <a
                                href="#solutions"
                                className="btn-secondary"
                                data-testid="hero-cta-secondary"
                            >
                                Explore Solutions
                            </a>
                        </div>

                        {/* Tracking widget */}
                        <div className="mt-8" data-testid="hero-track-widget">
                            <div className="eyebrow mb-3 flex items-center gap-2">
                                <span className="pulse-dot" /> Got a container?
                            </div>
                            <TrackWidget />
                        </div>

                        <div
                            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs font-mono-accent text-zinc-500"
                            data-testid="hero-trust-strip"
                        >
                            <TrustItem icon={<Anchor size={14} />} label="Port-to-port intelligence" />
                            <TrustItem icon={<Ship size={14} />} label="Carrier &amp; routing insights" />
                            <TrustItem icon={<span className="pulse-dot" />} label="Live data feeds" />
                        </div>
                    </div>

                    {/* Right column — floating stat card */}
                    <div className="lg:col-span-4">
                        <div className="glass rounded-2xl p-6 sm:p-7" data-testid="hero-stat-panel">
                            <div className="flex items-center justify-between">
                                <span className="eyebrow">Live · AU Trade Lane</span>
                                <span className="pulse-dot" />
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-y-6">
                                <Stat value="9+" label="Logistics gaps we close" />
                                <Stat value="3" label="Phases of shipment control" />
                                <Stat value="100%" label="Independent advisory" />
                                <Stat value="AU" label="Built for the AU market" />
                            </div>
                            <div className="mt-7 border-t border-white/10 pt-5">
                                <p className="text-[11px] font-mono-accent text-zinc-500">
                                    SYDNEY · MELBOURNE · BRISBANE · FREMANTLE · ADELAIDE
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TrustItem({ icon, label }) {
    return (
        <span className="inline-flex items-center gap-2 text-zinc-400">
            <span className="text-cyan-300">{icon}</span>
            <span dangerouslySetInnerHTML={{ __html: label }} />
        </span>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display text-3xl sm:text-4xl text-white">{value}</div>
            <div className="mt-1 text-xs text-zinc-500 leading-snug">{label}</div>
        </div>
    );
}
